import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { computed, inject, PLATFORM_ID, Service, signal } from '@angular/core';
import { LocalStorageService } from '@core/services/local-storage/local-storage.service';
import { LocalStorageKeys } from '@core/types/local-storage.type';
import { ThemeMode, ThemeModes } from '@core/types/theme.type';

@Service()
export class ThemeService {
  private readonly localStorageService = inject(LocalStorageService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);

  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly systemThemeQuery = this.isBrowser ? window.matchMedia('(prefers-color-scheme: dark)') : null;

  private readonly currentMode = signal<ThemeMode>(ThemeModes.System);
  public readonly mode = this.currentMode.asReadonly();

  public readonly isDark = computed(() => {
    const mode = this.currentMode();

    if (mode === ThemeModes.Dark) {
      return true;
    }

    if (mode === ThemeModes.Light) {
      return false;
    }

    if (!this.isBrowser) {
      return false;
    }

    return this.systemThemeQuery?.matches ?? false;
  });

  public initService(): void {
    if (!this.isBrowser) {
      return;
    }

    const modeInStorage = this.localStorageService.get<ThemeMode>(LocalStorageKeys.ThemeMode);
    const nextMode = this.isThemeMode(modeInStorage) ? modeInStorage : ThemeModes.System;

    this.currentMode.set(nextMode);
    this.applyMode(nextMode);

    this.systemThemeQuery?.addEventListener('change', () => {
      if (this.currentMode() === ThemeModes.System) {
        this.applyMode(ThemeModes.System);
      }
    });
  }

  public setMode(mode: ThemeMode): void {
    if (!this.isBrowser) {
      this.currentMode.set(mode);
      return;
    }

    this.currentMode.set(mode);
    this.localStorageService.set(LocalStorageKeys.ThemeMode, mode);
    this.applyMode(mode);
  }

  public cycleMode(): void {
    const order: ReadonlyArray<ThemeMode> = [ThemeModes.System, ThemeModes.Light, ThemeModes.Dark];
    const currentIndex = order.indexOf(this.currentMode());
    const nextIndex = (currentIndex + 1) % order.length;
    this.setMode(order[nextIndex]);
  }

  private applyMode(mode: ThemeMode): void {
    const root = this.document.documentElement;
    const resolvedMode = this.resolveMode(mode);

    root.removeAttribute('data-theme');
    root.classList.remove('theme-light', 'theme-dark');

    root.setAttribute('data-theme', resolvedMode);
    root.classList.add(resolvedMode === ThemeModes.Dark ? 'theme-dark' : 'theme-light');
  }

  private resolveMode(mode: ThemeMode): Exclude<ThemeMode, 'system'> {
    if (mode === ThemeModes.System) {
      return this.systemThemeQuery?.matches ? ThemeModes.Dark : ThemeModes.Light;
    }

    return mode;
  }

  private isThemeMode(value: unknown): value is ThemeMode {
    return value === ThemeModes.System || value === ThemeModes.Light || value === ThemeModes.Dark;
  }
}
